import React, { useMemo } from "react";
import { Inbox, ClipboardList, MessageSquare, Trophy, PhoneCall, MessageCircle } from "lucide-react";
import { BarChart, DonutChart, HBar } from "./charts";

const SOURCE_LABELS = {
  "header": "Header (desktop)",
  "header-mobile": "Header (mobile menu)",
  "mobile-bar": "Mobile Call bar",
  "footer": "Footer",
  "footer-cta": "Footer CTA",
  "page": "In-page button",
  "site": "Website",
};

export default function Overview({ leads, calls = [], counts }) {
  const analytics = useMemo(() => {
    // Leads over last 14 days
    const days = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      days.push({ key: d.toISOString().slice(0, 10), label: d.toLocaleDateString("en-US", { day: "numeric" }), value: 0 });
    }
    const dayMap = Object.fromEntries(days.map((d) => [d.key, d]));
    const byStatus = {}; const byService = {}; const byType = { estimate: 0, contact: 0 };
    let won = 0;
    leads.forEach((l) => {
      const day = (l.created_at || "").slice(0, 10);
      if (dayMap[day]) dayMap[day].value += 1;
      const st = l.status || "new"; byStatus[st] = (byStatus[st] || 0) + 1;
      byType[l.type] = (byType[l.type] || 0) + 1;
      if (st === "won") won += 1;
      const svc = l.service || (l.type === "contact" ? "General Inquiry" : "Unspecified");
      byService[svc] = (byService[svc] || 0) + 1;
    });
    const statusData = ["new", "contacted", "quoted", "won", "lost"].filter((s) => byStatus[s]).map((s) => ({ label: s, value: byStatus[s] }));
    const serviceData = Object.entries(byService).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 6);
    const typeData = [{ label: "Estimates", value: byType.estimate || 0 }, { label: "Contacts", value: byType.contact || 0 }];
    const total = leads.length || 1;
    const conv = Math.round((won / total) * 100);
    return { days, statusData, serviceData, typeData, conv, won };
  }, [leads]);

  const callAnalytics = useMemo(() => {
    const days = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      days.push({ key: d.toISOString().slice(0, 10), label: d.toLocaleDateString("en-US", { day: "numeric" }), value: 0 });
    }
    const dayMap = Object.fromEntries(days.map((d) => [d.key, d]));
    const bySource = {};
    calls.forEach((c) => {
      const day = (c.created_at || "").slice(0, 10);
      if (dayMap[day]) dayMap[day].value += 1;
      const src = c.source || "site"; bySource[src] = (bySource[src] || 0) + 1;
    });
    const sourceData = Object.entries(bySource).map(([label, value]) => ({ label: SOURCE_LABELS[label] || label, value })).sort((a, b) => b.value - a.value);
    const recent = [...calls].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 12);
    return { days, sourceData, recent };
  }, [calls]);

  const kpis = [
    { label: "Total Leads", value: counts.total || 0, icon: Inbox, color: "text-brand-ocean" },
    { label: "Estimate Requests", value: counts.estimate || 0, icon: ClipboardList, color: "text-brand-gold" },
    { label: "Contact Messages", value: counts.contact || 0, icon: MessageSquare, color: "text-brand-sky" },
    { label: "Click to Call", value: counts.calls || 0, icon: PhoneCall, color: "text-green-600" },
    { label: "Text Taps", value: counts.texts || 0, icon: MessageCircle, color: "text-brand-ocean" },
  ];

  const fmtTime = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-2xl bg-white p-5 ring-1 ring-brand-mist shadow-card">
              <div className="flex items-center justify-between">
                <span className={`grid place-items-center h-11 w-11 rounded-xl bg-brand-mist ${k.color}`}><Icon className="h-6 w-6" /></span>
              </div>
              <div className="mt-3 text-3xl font-heading font-extrabold text-brand-navy">{k.value}</div>
              <div className="text-xs text-brand-slate font-medium mt-0.5">{k.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl bg-white p-6 ring-1 ring-brand-mist shadow-card">
          <h3 className="font-heading font-extrabold text-brand-navy">Leads - Last 14 Days</h3>
          <p className="text-xs text-brand-slate mb-4">New submissions per day</p>
          <BarChart data={analytics.days} />
        </div>
        <div className="rounded-2xl bg-white p-6 ring-1 ring-brand-mist shadow-card">
          <h3 className="font-heading font-extrabold text-brand-navy">By Status</h3>
          <p className="text-xs text-brand-slate mb-4">Pipeline breakdown</p>
          {analytics.statusData.length ? <DonutChart data={analytics.statusData} /> : <p className="text-sm text-brand-slate py-8 text-center">No data yet</p>}
        </div>
      </div>

      {/* Click-to-Call activity */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl bg-white p-6 ring-1 ring-brand-mist shadow-card" data-testid="calls-trend-card">
          <div className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5 text-green-600" />
            <h3 className="font-heading font-extrabold text-brand-navy">Call &amp; Text - Last 14 Days</h3>
          </div>
          <p className="text-xs text-brand-slate mb-4">Contact taps per day · <span className="text-green-600 font-bold">{counts.calls || 0} calls</span> · <span className="text-brand-ocean font-bold">{counts.texts || 0} texts</span></p>
          <BarChart data={callAnalytics.days} color="#22C55E" />
          {callAnalytics.sourceData.length > 0 && (
            <div className="mt-6 pt-5 border-t border-brand-mist">
              <h4 className="text-xs font-heading font-bold text-brand-slate uppercase tracking-wide mb-3">Top Tap Sources</h4>
              <HBar data={callAnalytics.sourceData} color="#22C55E" />
            </div>
          )}
        </div>
        <div className="rounded-2xl bg-white p-6 ring-1 ring-brand-mist shadow-card" data-testid="calls-recent-card">
          <h3 className="font-heading font-extrabold text-brand-navy">Recent Taps</h3>
          <p className="text-xs text-brand-slate mb-4">Latest call &amp; text taps</p>
          {callAnalytics.recent.length ? (
            <ul className="space-y-3">
              {callAnalytics.recent.map((c, i) => {
                const isText = c.kind === "text";
                const Icon = isText ? MessageCircle : PhoneCall;
                return (
                  <li key={c.id || i} className="flex items-start gap-3">
                    <span className={`grid place-items-center h-8 w-8 rounded-lg shrink-0 ${isText ? "bg-brand-mist text-brand-ocean" : "bg-green-50 text-green-600"}`}><Icon className="h-4 w-4" /></span>
                    <div className="min-w-0">
                      <div className="text-sm font-heading font-bold text-brand-navy truncate">
                        {SOURCE_LABELS[c.source] || c.source || "Website"}
                        <span className={`ml-2 align-middle text-[10px] font-bold px-1.5 py-0.5 rounded ${isText ? "bg-brand-ocean/10 text-brand-ocean" : "bg-green-100 text-green-700"}`}>{isText ? "TEXT" : "CALL"}</span>
                      </div>
                      <div className="text-xs text-brand-slate">{fmtTime(c.created_at)}{c.path ? ` · ${c.path}` : ""}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : <p className="text-sm text-brand-slate py-8 text-center">No call or text taps yet</p>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl bg-white p-6 ring-1 ring-brand-mist shadow-card">
          <h3 className="font-heading font-extrabold text-brand-navy">Top Requested Services</h3>
          <p className="text-xs text-brand-slate mb-4">Across all leads</p>
          {analytics.serviceData.length ? <HBar data={analytics.serviceData} /> : <p className="text-sm text-brand-slate py-8 text-center">No data yet</p>}
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl bg-brand-navy text-white p-6 shadow-card">
            <div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-brand-gold2" /><span className="font-heading font-bold">Win Rate</span></div>
            <div className="mt-3 text-4xl font-heading font-extrabold text-brand-gold2">{analytics.conv}%</div>
            <p className="text-white/60 text-xs mt-1">{analytics.won} won of {leads.length} leads</p>
          </div>
          <div className="rounded-2xl bg-white p-6 ring-1 ring-brand-mist shadow-card">
            <h3 className="font-heading font-extrabold text-brand-navy mb-4">Estimates vs Contacts</h3>
            <HBar data={analytics.typeData} color="#F5A623" />
          </div>
        </div>
      </div>
    </div>
  );
}
